import React, { useEffect, useState } from 'react';
import { Building2 } from 'lucide-react';
import api from '../../lib/api';
import type { CFQuery, CFEntity, CFVendorPayersPayload } from '../../types/campaignFinance';
import { useCFEntity, type CFEntityScope } from '../../hooks/useCFEntity';
import { fmtMoney, CF_REQUEST_TIMEOUT_MS } from '../../utils/campaignFinanceUtils';
import CFTransactionsExplorer from './CFTransactionsExplorer';
import CommitteeLink from '../committee/CommitteeLink';

interface CFVendorDashboardProps {
  /** The searched payee display name (free text or a typeahead pick). */
  payeeName: string;
  /** Resolved vendor identity — set by a typeahead pick or a restored URL. */
  vendorId: string | null;
  query: CFQuery;
  cycleLabel?: string;
  onEntitySearch?: (kind: 'contributor' | 'committee', name: string) => void;
}

/**
 * The vendor landing view — where a payee-centric search arrives. Vendors are terminal
 * (TRACER has no sub-vendor data), so this is the aggregation path, not a network:
 * identity header, the who-paid-them list (same payers payload as the panel expansion),
 * and the expenditures ledger scoped to this payee.
 *
 * With a resolved vendor_id, scoping is exact and spelling-proof. Without one (free-text
 * payee that never got a typeahead pick), the header and payers are unavailable and the
 * ledger falls back to name matching — shown as reported, stated as such.
 */
const CFVendorDashboard: React.FC<CFVendorDashboardProps> = ({ payeeName, vendorId, query, cycleLabel, onEntitySearch }) => {
  const expenditures = useCFEntity('expenditures');
  // Instantiated but never loaded — the explorer's entities prop types all three,
  // and the tabs prop limits rendering to expenditures.
  const contributions = useCFEntity('contributions');
  const loans = useCFEntity('loans');
  const { load: loadExp } = expenditures;
  const [activeTab, setActiveTab] = useState<CFEntity>('expenditures');

  const [payers, setPayers] = useState<CFVendorPayersPayload | null>(null);
  const [payersLoading, setPayersLoading] = useState(false);
  const [payersError, setPayersError] = useState<string | null>(null);

  // "Filed under N names" popover. Hover opens it; click PINS it — hover alone fails
  // touch (phone browsers get this web UI) and hover-only content fails accessibility.
  const [filedAsHover, setFiledAsHover] = useState(false);
  const [filedAsPinned, setFiledAsPinned] = useState(false);
  const filedAsOpen = filedAsHover || filedAsPinned;

  // Who paid this vendor — only answerable against a resolved identity.
  useEffect(() => {
    if (!vendorId) { setPayers(null); return; }
    let cancelled = false;
    setPayersLoading(true);
    setPayersError(null);
    setPayers(null);
    const params = new URLSearchParams();
    if (query.start_date) params.set('start_date', query.start_date);
    if (query.end_date) params.set('end_date', query.end_date);
    const qs = params.toString();
    api.get(`/campaign-finance/vendors/${encodeURIComponent(vendorId)}/payers${qs ? `?${qs}` : ''}`, { timeout: CF_REQUEST_TIMEOUT_MS })
      .then(res => {
        if (cancelled) return;
        if (res.data?.success) setPayers(res.data.data as CFVendorPayersPayload);
        else setPayersError(res.data?.error || 'Failed to load payers');
      })
      .catch(err => { if (!cancelled) setPayersError(err?.response?.data?.error || err?.message || 'Failed to load payers'); })
      .finally(() => { if (!cancelled) setPayersLoading(false); });
    return () => { cancelled = true; };
  }, [vendorId, query.start_date, query.end_date]);

  // The ledger: exact vendor_id scope when resolved; raw payee-name scope otherwise.
  useEffect(() => {
    const scope: CFEntityScope = {
      ...(vendorId ? { vendor_id: vendorId } : { payee: payeeName }),
      expenditure_type: query.expenditure_type || undefined,
      payment_type: query.payment_type || undefined,
      disbursement_type: query.disbursement_type || undefined,
      min_amount: query.min_amount || undefined,
      max_amount: query.max_amount || undefined,
      start_date: query.start_date || undefined,
      end_date: query.end_date || undefined,
    };
    loadExp(scope, 'none');
    setActiveTab('expenditures');
  }, [vendorId, payeeName, query.expenditure_type, query.payment_type, query.disbursement_type, query.min_amount, query.max_amount, query.start_date, query.end_date, loadExp]);

  const vendor = payers?.vendor;
  const isBadgedCommittee = vendor?.linked_co_id != null && (vendor.link_confidence ?? 0) >= 0.85;
  const maxPayer = Math.max(1, ...(payers?.payers.map(p => p.total) ?? [1]));

  return (
    <div className="space-y-[18px]">
      {/* Follow the money — inbound. The vendor is a TERMINUS, so the node sits on the
          RIGHT with payers flowing into it: money always reads left → right, and the
          entity node sits at its true position in the flow (contributor = left source,
          committee = middle, vendor = right terminus). Bars are purple because these
          are expenditure records — the green/purple grammar is by table, not by
          direction relative to the viewed entity. */}
      <div className="card">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-accent-500 dark:text-text-dark-tertiary mb-4">
          Follow the money — inbound{cycleLabel ? ` · ${cycleLabel} cycle` : ''}
        </div>

        {!vendorId ? (
          <div className="flex items-center gap-2 text-[12.5px] text-text-secondary dark:text-text-dark-secondary">
            <Building2 className="w-4 h-4 flex-none text-cf-out" />
            <span className="font-semibold text-text-primary dark:text-text-dark-primary">{payeeName}</span>
            <span className="text-text-tertiary dark:text-text-dark-tertiary">— no confirmed vendor identity for this name; the ledger below matches the payee name as reported.</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_200px] gap-[18px] items-stretch">
            {/* Paid by — every committee, coverage stated. */}
            <div>
              <div className="flex items-center gap-[7px] mb-3">
                <span className="w-2 h-2 rounded-full bg-cf-out" />
                <span className="text-xs font-semibold text-primary-700 dark:text-primary-dark-400">Paid by · top committees</span>
              </div>
              {payersLoading && <div className="text-[12px] text-text-tertiary dark:text-text-dark-tertiary py-2">Loading…</div>}
              {payersError && <div className="text-[12px] text-cf-alert py-2">{payersError}</div>}
              {payers && (
                <>
                  <div className="flex flex-col gap-2">
                    {payers.payers.map(p => (
                      <div key={p.co_id}>
                        <div className="flex justify-between items-baseline gap-2 mb-[3px]">
                          <CommitteeLink
                            coId={p.co_id}
                            name={p.name}
                            className="text-[12.5px] text-text-primary dark:text-text-dark-primary truncate min-w-0 flex-1 hover:text-primary-700 dark:hover:text-primary-dark-400"
                          />
                          <span className="tabular-nums text-xs font-semibold flex-none text-primary-700 dark:text-primary-dark-400">
                            {p.share >= 0.001 ? `${(p.share * 100).toFixed(1)}% ` : ''}{fmtMoney(p.total, { compact: true })}
                          </span>
                        </div>
                        <div className="h-1.5 rounded-full overflow-hidden bg-cf-out-soft">
                          <div className="h-full rounded-full" style={{ width: `${Math.max(2, (p.total / maxPayer) * 100)}%`, background: 'var(--cf-out)' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  {payers.coverage.total_committees > payers.coverage.shown_committees && (
                    <div className="text-[10.5px] text-text-tertiary dark:text-text-dark-tertiary mt-2">
                      showing top {payers.coverage.shown_committees} of {payers.coverage.total_committees} committees
                      {payers.coverage.shown_pct != null && <> · {Math.round(payers.coverage.shown_pct * 100)}% of dollars</>}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* The terminus node — money arrives here and stops. */}
            <div className="flex flex-col items-center justify-center gap-2">
              <svg width="100%" height="22" viewBox="0 0 200 22" preserveAspectRatio="none" className="hidden lg:block">
                <line x1="2" y1="11" x2="186" y2="11" stroke="var(--cf-out)" strokeWidth="2" />
                <path d="M180 5 L192 11 L180 17" fill="none" stroke="var(--cf-out)" strokeWidth="2" />
              </svg>
              <div className="rounded-[14px] px-3 py-3.5 text-center w-full text-white" style={{ background: 'var(--cf-out-deep)', boxShadow: '0 6px 20px rgba(62,39,80,.3)' }}>
                <div className="text-[11px] font-semibold tracking-[0.04em] text-cf-out-pale">PAYEE</div>
                <div className="font-semibold text-[13px] leading-tight mt-[3px] truncate flex items-center justify-center gap-1.5" title={vendor?.name || payeeName}>
                  {vendor?.name || payeeName}
                  {isBadgedCommittee && <span className="rounded-full bg-white/15 text-cf-out-mid px-1.5 text-[9.5px] font-semibold flex-none">Committee</span>}
                </div>
                {payers && (
                  <div className="flex justify-center gap-3 mt-2.5">
                    <div>
                      <div className="text-[9px] font-semibold text-cf-out-muted">RECEIVED</div>
                      <div className="font-bold text-sm text-cf-out-mid tabular-nums">{fmtMoney(payers.totals.total, { compact: true })}</div>
                    </div>
                    <div className="w-px bg-white/20" />
                    <div>
                      <div className="text-[9px] font-semibold text-cf-out-muted">FROM</div>
                      <div className="font-bold text-sm text-white tabular-nums">{payers.totals.committees}<span className="text-[9px] font-semibold text-cf-out-muted ml-0.5">CMTE{payers.totals.committees === 1 ? '' : 'S'}</span></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Consolidation disclosure — only when there is genuinely more than one
                  reported spelling. The consolidation is a claim we make; the filed
                  spellings are how the claim stays checkable. */}
              {payers && payers.filed_as && payers.filed_as.count > 1 && (
                <div
                  className="relative"
                  onMouseEnter={() => setFiledAsHover(true)}
                  onMouseLeave={() => setFiledAsHover(false)}
                >
                  <button
                    type="button"
                    aria-expanded={filedAsOpen}
                    onClick={() => setFiledAsPinned(p => !p)}
                    className="text-[10.5px] text-text-tertiary dark:text-text-dark-tertiary underline decoration-dotted underline-offset-2 hover:text-primary-700 dark:hover:text-primary-dark-400"
                  >
                    Filed under {payers.filed_as.count} names
                  </button>
                  {filedAsOpen && (
                    <div className="absolute right-0 top-full mt-1.5 z-40 w-72 max-h-72 overflow-y-auto rounded-[10px] border border-border-light dark:border-border-dark bg-white dark:bg-background-dark-secondary shadow-lg p-3">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-text-tertiary dark:text-text-dark-tertiary mb-2">
                        Reported spellings · all filings
                      </div>
                      <div className="flex flex-col gap-1">
                        {payers.filed_as.names.map(f => (
                          <div key={f.name} className="flex justify-between items-baseline gap-2">
                            <span className="text-[11.5px] text-text-primary dark:text-text-dark-primary truncate min-w-0" title={f.name}>{f.name}</span>
                            <span className="tabular-nums text-[10.5px] font-semibold flex-none text-primary-700 dark:text-primary-dark-400">{fmtMoney(f.total, { compact: true })}</span>
                          </div>
                        ))}
                      </div>
                      {payers.filed_as.count > payers.filed_as.names.length && (
                        <div className="text-[10px] text-text-tertiary dark:text-text-dark-tertiary mt-1.5">
                          showing top {payers.filed_as.names.length} of {payers.filed_as.count} spellings
                        </div>
                      )}
                      <div className="text-[10px] text-text-tertiary dark:text-text-dark-tertiary mt-2 pt-2 border-t border-border-light dark:border-border-dark">
                        Consolidated by normalized spelling — each ledger row below shows its name exactly as filed.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* The ledger, scoped to this payee. */}
      <CFTransactionsExplorer
        activeTab={activeTab}
        onTabChange={setActiveTab}
        entities={{ contributions, expenditures, loans }}
        cycleLabel={cycleLabel}
        tabs={['expenditures']}
        onEntitySearch={onEntitySearch}
      />
    </div>
  );
};

export default CFVendorDashboard;
